'use client';

import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@awesome.me/kit-94e593bf23/icons/sharp/regular';
import { faCheck } from '@awesome.me/kit-94e593bf23/icons/sharp/solid';
import { useDropdown } from '@/app/_lib/hooks';
import Price from '@/app/_components/Price';

/**
 * ProductOptionSelect - Dropdown for product option selection
 *
 * Purpose-built for product options with:
 * - Option name and price display
 * - Optional thumbnail images
 * - "Edit" hover indicator when option is selected
 * - Validation/error state support
 */
export default function ProductOptionSelect(t0) {
  const $ = _c(34);
  const {
    identifier,
    label,
    options: t1,
    selectedOption,
    selectedValue,
    onSelect,
    required: t2,
    invalid: t3,
    className: t4,
    ariaDescribedBy
  } = t0;
  const options = t1 === undefined ? [] : t1;
  const required = t2 === undefined ? false : t2;
  const invalid = t3 === undefined ? false : t3;
  const className = t4 === undefined ? "" : t4;
  const sortedOptions = [...options].sort(_temp);
  const {
    isOpen,
    focusedIndex,
    containerRef,
    getTriggerProps,
    getListProps,
    getItemProps
  } = useDropdown({
    items: sortedOptions,
    onSelect,
    selectedValue,
    getItemValue: _temp2,
    isItemSelectable: _temp3,
    identifier,
    preventOutsideClickPropagation: true
  });
  const hasSelection = selectedOption && selectedValue;
  const t5 = `group relative min-h-[1.5rem] border-b ${isOpen ? "border-primary" : "border-gray-10"} hover:border-primary ${invalid ? "border-red" : ""} ${className}`;
  const t6 = `${identifier}-label`;
  const t7 = required && "(required)";
  let t8;
  if ($[0] !== label || $[1] !== t6 || $[2] !== t7) {
    t8 = <div id={t6} className="sr-only">{label} {t7}</div>;
    $[0] = label;
    $[1] = t6;
    $[2] = t7;
    $[3] = t8;
  } else {
    t8 = $[3];
  }
  const t9 = `flex items-center justify-between w-full min-h-[44px] md:min-h-[2rem] py-2 md:py-1 transition-all duration-100 bg-transparent border-none text-left focus:outline-none relative ${isOpen ? "z-[1000] text-primary" : ""}`;
  const t10 = `${identifier}-label`;
  const t11 = required ? "true" : undefined;
  const t12 = invalid ? "true" : undefined;
  let t13;
  if ($[4] !== getTriggerProps) {
    t13 = getTriggerProps();
    $[4] = getTriggerProps;
    $[5] = t13;
  } else {
    t13 = $[5];
  }
  let t14;
  if ($[6] !== hasSelection || $[7] !== isOpen || $[8] !== label || $[9] !== selectedOption) {
    t14 = <span className="transition-all duration-500 text-sm md:text-base flex-1">{hasSelection ? <><span className="absolute -top-[.9rem] indent-0 text-xs capitalize text-light">{label} <FontAwesomeIcon icon={faCheck} className="text-primary text-2xs ml-0.5" /></span><div><span className="capitalize text-dark group-hover:text-light-3 transition-colors duration-500">{selectedOption.option_name}</span>{selectedOption.option_price > 0 && <span className="text-primary group-hover:text-light-3 transition-colors duration-500 ml-1">+ <Price amount={selectedOption.option_price} /></span>}</div></> : <span className={`capitalize text-sm md:text-md text-light group-hover:pl-1 transition-all duration-500 ${isOpen ? "pl-1" : ""}`}>{label}</span>}</span>;
    $[6] = hasSelection;
    $[7] = isOpen;
    $[8] = label;
    $[9] = selectedOption;
    $[10] = t14;
  } else {
    t14 = $[10];
  }
  let t15;
  if ($[11] !== hasSelection || $[12] !== selectedOption) {
    t15 = hasSelection && selectedOption.option_photo && <Image src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/option_photos/${selectedOption.option_photo}`} alt={selectedOption.option_name} width={36} height={20} className="w-9 h-5" />;
    $[11] = hasSelection;
    $[12] = selectedOption;
    $[13] = t15;
  } else {
    t15 = $[13];
  }
  const t16 = `text-xs text-light transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`;
  let t17;
  if ($[14] !== t16) {
    t17 = <FontAwesomeIcon icon={faChevronRight} className={t16} aria-hidden="true" />;
    $[14] = t16;
    $[15] = t17;
  } else {
    t17 = $[15];
  }
  let t18;
  if ($[16] !== t15 || $[17] !== t17) {
    t18 = <div className="flex items-center gap-2">{t15}{t17}</div>;
    $[16] = t15;
    $[17] = t17;
    $[18] = t18;
  } else {
    t18 = $[18];
  }
  let t19;
  if ($[19] !== ariaDescribedBy || $[20] !== t10 || $[21] !== t11 || $[22] !== t12 || $[23] !== t13 || $[24] !== t14 || $[25] !== t18 || $[26] !== t9) {
    t19 = <button type="button" className={t9} aria-labelledby={t10} aria-required={t11} aria-invalid={t12} aria-describedby={ariaDescribedBy} {...t13}>{t14}{t18}</button>;
    $[19] = ariaDescribedBy;
    $[20] = t10;
    $[21] = t11;
    $[22] = t12;
    $[23] = t13;
    $[24] = t14;
    $[25] = t18;
    $[26] = t9;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  const t20 = isOpen && <ul className="absolute top-full left-0 w-full bg-white border border-gray-10 shadow-xl z-[1060] max-h-[400px] overflow-y-auto" {...getListProps()}>{sortedOptions.map((option, index) => {
      if (!option.option_name) {
        return <li key={option.option_id || `separator-${index}`} role="separator" className="border-b-2 border-gray-10 my-1" aria-hidden="true" />;
      }
      return <li key={option.option_id} {...getItemProps(option, index)}><div className={`grid grid-cols-[1fr_auto_auto] gap-x-3 items-center w-full py-3 pl-3 pr-7 md:py-2 md:pl-2 md:pr-5 bg-white text-left text-base hover:bg-gray-5 cursor-pointer ${focusedIndex === index ? "bg-gray-5" : ""} ${index < sortedOptions.length - 1 ? "border-b border-gray-5" : ""}`}><span className="text-dark">{option.option_name}</span><span className={`whitespace-nowrap ${option.option_price > 0 ? "text-primary" : "text-transparent"}`}>+<Price amount={option.option_price || 0} /></span>{option.option_photo ? <Image src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/option_photos/${option.option_photo}`} alt={option.option_name} width={36} height={20} className="w-9 h-5" /> : <span className="w-9" />}</div></li>;
    })}</ul>;
  let t21;
  if ($[28] !== containerRef || $[29] !== t19 || $[30] !== t20 || $[31] !== t5 || $[32] !== t8) {
    t21 = <div ref={containerRef} className={t5}>{t8}{t19}{t20}</div>;
    $[28] = containerRef;
    $[29] = t19;
    $[30] = t20;
    $[31] = t5;
    $[32] = t8;
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  return t21;
}
function _temp3(item_0) {
  return !!item_0.option_name;
}
function _temp2(item) {
  return item.option_id;
}
function _temp(a, b) {
  return (a.display_order ?? 0) - (b.display_order ?? 0);
}